/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { Tab, Typography } from '@material-ui/core';
import WelcomeSection from 'components/welcomeSection/WelcomeSection';
import Button from 'components/common/Button';
import useUserMenu from 'layout/menu/useUserMenu';
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded';
import { DashboardCard } from './components/DashboardCard';
import { useDashboard } from '../../redux/features/dashboard/useDashboard';

const useStyles = makeStyles(theme => ({
  cardSection: {
    display: 'grid',
    rowGap: '1rem',
    [theme.breakpoints.up('sm')]: {
      rowGap: '1.75rem',
    },
    [theme.breakpoints.up('md')]: {
      rowGap: '1.75rem',
      columnGap: '1rem',
    },
    [theme.breakpoints.up('lg')]: {
      rowGap: '1.75rem',
      columnGap: '1rem',
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.up('xl2')]: {
      columnGap: '2.938rem',
      rowGap: '2.625rem',
    },
    [theme.breakpoints.up('xl3')]: {
      columnGap: '6.25rem',
    },
    [theme.breakpoints.up('xl4')]: {
      columnGap: '7.125rem',
    },
  },
  tabList: {
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.tabSectionUnderline,
    borderBottomWidth: '1px',
  },
  errorIcon: {
    fontSize: '1.7rem',
    color: theme.palette.failed,
    marginRight: '1rem',
    marginBottom: '0.5rem',
  },
  errorContainer: {
    width: '100%',
    display: 'flex',
    marginTop: '1.5rem',
    flexDirection: 'column',
  },
  errorTitle: {
    fontWeight: 600,
    marginBottom: '0.5rem',
    fontSize: '1.2rem',
  },
  errorContent: {
    fontSize: '1.2rem',
  },
  retryButton: {
    marginTop: '1rem',
    maxWidth: 'fit-content',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
}));

export default function Dashboard() {
  const { t } = useTranslation();
  const classes = useStyles();

  document.title = 'Dashboard - Patent E-filing';
  const { categories, activeCategory, loadingError, onTabChange } = useDashboard();
  const { userData } = useUserMenu();

  return (
    <>
      <WelcomeSection
        title={t('dashboard.welcome')
          .concat(' ')
          .concat(userData?.name)}
        description={t('messages.dashboard.national.patent.portal')}
      />
      {loadingError && (
        <div className={classes.errorContainer}>
          <div className={classes.titleContainer}>
            <ErrorRoundedIcon className={classes.errorIcon} />
            <Typography className={classes.errorTitle}>{t('loading.error.title')}</Typography>
          </div>

          <Typography className={classes.errorContent} variant="body2">
            {t('dashboard.loading.error.content')}
          </Typography>

          <Button color="primary" id="retry" className={classes.retryButton} onClick={() => window.location.reload()}>
            {t('actions.try.again')}
          </Button>
        </div>
      )}

      {activeCategory && !loadingError && (
        <TabContext value={activeCategory}>
          <div className={classes.tabList}>
            <TabList
              onChange={onTabChange}
              aria-label="Procedures"
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {categories?.map(({ label }) => (
                <Tab key={label} fullWidth label={t(label)} value={label} />
              ))}
            </TabList>
          </div>

          {categories?.map((category, categoryIndex) => {
            return (
              <TabPanel value={category?.label} key={`category-${categoryIndex}`}>
                <div className={classes.cardSection}>
                  {category?.procedureIds?.map(procedureId => (
                    <DashboardCard key={`procedure-${procedureId}`} procedureId={procedureId} />
                  ))}
                </div>
              </TabPanel>
            );
          })}
        </TabContext>
      )}
    </>
  );
}
